import axios from 'axios';
import * as tokenStorage from './tokenStorage';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_NOTIFICATION_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let onAuthFailureCallback = null;
let isRefreshing = false;
let failedQueue = [];

export function setOnAuthFailure(callback) {
  onAuthFailureCallback = callback;
}

function processQueue(error, token) {
  failedQueue.forEach((item) => {
    if (error) {
      item.reject(error);
    } else {
      item.resolve(token);
    }
  });
  failedQueue = [];
}

async function performTokenRefresh() {
  const refreshToken = tokenStorage.getRefreshToken();
  const response = await axios.post(
    `${process.env.REACT_APP_NOTIFICATION_BASE_URL}/auth/refresh`,
    { refreshToken },
    { headers: { 'Content-Type': 'application/json' } }
  );

  const { accessToken, refreshToken: newRefreshToken, expiresIn } = response.data;
  tokenStorage.setTokens(accessToken, newRefreshToken, expiresIn);
  return accessToken;
}

apiClient.interceptors.request.use(async (config) => {
  if (config._skipAuth === true) {
    return config;
  }

  const refreshToken = tokenStorage.getRefreshToken();
  if (tokenStorage.getAccessToken() && tokenStorage.isTokenExpiringSoon() && refreshToken) {
    try {
      await performTokenRefresh();
    } catch (_error) {
      // Proactive refresh failed; continue with current token.
      // The response interceptor will handle 401 if the token is actually expired.
    }
  }

  const token = tokenStorage.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config;

    if (!error.response || error.response.status !== 401) {
      return Promise.reject(error);
    }

    if (originalRequest._skipAuth === true) {
      return Promise.reject(error);
    }

    if (originalRequest._retry === true) {
      return Promise.reject(error);
    }

    if (originalRequest.url && originalRequest.url.includes('/auth/refresh')) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return apiClient(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    return new Promise((resolve, reject) => {
      performTokenRefresh()
        .then((newToken) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);
          resolve(apiClient(originalRequest));
        })
        .catch((refreshError) => {
          processQueue(refreshError, null);
          if (onAuthFailureCallback) {
            onAuthFailureCallback();
          }
          reject(refreshError);
        })
        .finally(() => {
          isRefreshing = false;
        });
    });
  }
);

export function extractErrorMessage(error) {
  if (error.response?.data?.detail) {
    return error.response.data.detail;
  }
  if (error.response?.data?.title) {
    return error.response.data.title;
  }
  if (error.message === 'Network Error') {
    return 'Ошибка сети. Проверьте подключение к интернету.';
  }
  return 'Произошла непредвиденная ошибка. Попробуйте позже.';
}

export default apiClient;
